import { CopyOutlined } from "@ant-design/icons";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Button, DatePicker, Descriptions, Drawer, Input, InputNumber, Space, Switch, Table, Tag, Typography, message } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { getPromoCode, listPromoPlans, promoErrorMessage, updatePromoCode } from "../../actions/promo_actions";
import { PERIOD_LABELS, PROMO_STATE_TAGS, describeDiscount, promoState, usageLabel } from "../../utils/promo";

const formatDate = (value) =>
  value ? new Intl.DateTimeFormat("en", { year: "numeric", month: "short", day: "numeric" }).format(new Date(value)) : "—";

const REDEMPTION_TAGS = {
  RESERVED: { color: "gold", label: "Checkout open" },
  ACTIVE: { color: "green", label: "Running" },
  COMPLETED: { color: "default", label: "Finished" },
  VOIDED: { color: "default", label: "Abandoned" },
};

/** One promo code: what it gives, who used it, and the settings that can still change. */
export default function PromoCodeDrawer({ promoId, onClose, onUpdated }) {
  const { data: promo, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["promo-code", promoId],
    queryFn: () => getPromoCode(promoId),
    enabled: !!promoId,
    retry: false,
  });

  const { data: plans = [] } = useQuery({
    queryKey: ["promo-plans"],
    queryFn: listPromoPlans,
    enabled: !!promoId,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const planNames = (ids) =>
    ids?.length ? ids.map((id) => plans.find((plan) => plan.id === id)?.name || id).join(", ") : "All plans";

  const [maxRedemptions, setMaxRedemptions] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [description, setDescription] = useState("");

  useEffect(() => {
    setMaxRedemptions(promo?.max_redemptions ?? null);
    setExpiresAt(promo?.expires_at ? dayjs(promo.expires_at) : null);
    setDescription(promo?.description || "");
  }, [promo]);

  const mutation = useMutation({
    mutationFn: (changes) => updatePromoCode(promoId, changes),
    onSuccess: () => {
      message.success("Promo code updated");
      refetch();
      onUpdated?.();
    },
    onError: (err) => message.error(promoErrorMessage(err)),
  });

  const state = promo ? promoState(promo) : null;
  const settingsChanged =
    promo &&
    ((maxRedemptions ?? null) !== (promo.max_redemptions ?? null) ||
      (expiresAt ? expiresAt.toISOString() : null) !== (promo.expires_at ? dayjs(promo.expires_at).toISOString() : null) ||
      description !== (promo.description || ""));

  return (
    <Drawer open={!!promoId} onClose={onClose} width={640} title={promo ? `Promo code ${promo.code}` : "Promo code"} destroyOnClose>
      {isError && <Alert type="error" showIcon message="Failed to load the promo code" description={promoErrorMessage(error)} />}
      {isLoading && <p>Loading…</p>}
      {promo && (
        <div className="promo-drawer">
          <div className="promo-drawer__headline">
            <Typography.Title level={3} copyable={{ text: promo.code, icon: <CopyOutlined /> }}>
              {promo.code}
            </Typography.Title>
            <Tag color={PROMO_STATE_TAGS[state].color}>{PROMO_STATE_TAGS[state].label}</Tag>
          </div>
          <p className="promo-drawer__summary">{describeDiscount(promo)}.</p>

          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="Billing">{promo.billing_periods.map((p) => PERIOD_LABELS[p]).join(", ")}</Descriptions.Item>
            <Descriptions.Item label="Usage">{usageLabel(promo)}</Descriptions.Item>
            <Descriptions.Item label="Plans" span={2}>{planNames(promo.plan_ids)}</Descriptions.Item>
            <Descriptions.Item label="Starts">{formatDate(promo.valid_from)}</Descriptions.Item>
            <Descriptions.Item label="Ends">{formatDate(promo.expires_at)}</Descriptions.Item>
            <Descriptions.Item label="Created by" span={2}>
              {promo.created_by || "—"} · {formatDate(promo.created_at)}
            </Descriptions.Item>
          </Descriptions>

          <section className="promo-drawer__section">
            <div className="promo-drawer__section-head">
              <h3>Availability</h3>
              <Space>
                <span>{promo.active ? "Accepting new signups" : "Paused"}</span>
                <Switch
                  checked={promo.active}
                  loading={mutation.isPending}
                  onChange={(checked) => mutation.mutate({ active: checked })}
                />
              </Space>
            </div>
            <p className="promo-drawer__hint">
              Pausing stops new signups from using the code. Vendors who already used it keep their discount.
            </p>
            <div className="promo-drawer__fields">
              <label>
                Usage limit
                <InputNumber min={1} precision={0} value={maxRedemptions} onChange={setMaxRedemptions} placeholder="Unlimited" />
              </label>
              <label>
                Ends
                <DatePicker value={expiresAt} onChange={setExpiresAt} placeholder="No end date" />
              </label>
              <label className="promo-drawer__note">
                Internal note
                <Input.TextArea rows={2} maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />
              </label>
            </div>
            <Button
              type="primary"
              disabled={!settingsChanged}
              loading={mutation.isPending}
              onClick={() =>
                mutation.mutate({
                  maxRedemptions: maxRedemptions ?? null,
                  expiresAt: expiresAt ? expiresAt.endOf("day").toISOString() : null,
                  description: description || null,
                })
              }
            >
              Save changes
            </Button>
            <p className="promo-drawer__hint">The discount and its number of payments cannot change; pause this code and create a new one instead.</p>
          </section>

          <section className="promo-drawer__section">
            <h3>Vendors using this code</h3>
            <Table
              size="small"
              rowKey="id"
              dataSource={promo.redemptions || []}
              pagination={false}
              locale={{ emptyText: "Nobody has used this code yet" }}
              columns={[
                {
                  title: "Vendor",
                  dataIndex: "account_urn",
                  render: (value, row) =>
                    value ? (
                      <div>
                        <strong>{row.account_name || "Unknown account"}</strong>
                        <div className="mono-cell subscription-secondary">{value}</div>
                      </div>
                    ) : (
                      "Not paid yet"
                    ),
                },
                {
                  title: "Status",
                  dataIndex: "status",
                  render: (value) => <Tag color={REDEMPTION_TAGS[value]?.color}>{REDEMPTION_TAGS[value]?.label || value}</Tag>,
                },
                {
                  title: "Discounted payments",
                  render: (_, row) => `${row.cycles_applied} of ${row.duration_cycles}`,
                },
                { title: "Started", dataIndex: "created_at", render: formatDate },
              ]}
            />
          </section>
        </div>
      )}
    </Drawer>
  );
}
